<template>
  <div class="sso-loading-container">
    <!-- Halo Background Elements -->
    <div class="halo-background">
      <div class="orb orb-1" />
      <div class="orb orb-2" />
      <div class="orb orb-3" />
    </div>

    <!-- Floating Particles -->
    <div class="particles">
      <div
        v-for="n in 12" :key="n" class="particle"
        :class="`p-${n}`"
      />
    </div>

    <div class="glass-card">
      <div class="loader-container">
        <div class="cube-loader">
          <div class="cube cube1" />
          <div class="cube cube2" />
          <div class="cube cube3" />
          <div class="cube cube4" />
        </div>

        <div class="text-area">
          <h2 class="status-title">{{ loadingText }}</h2>
          <p class="status-subtitle">{{ subtitleText }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getSsoAuthUrl, getUserIpInformation } from '@/api/user';
import { computer } from '@/utils/current_browser_util';
import { Base64 } from 'js-base64';

export default {
  name: 'SsoLogin',
  data() {
    return {
      loadingText: this.$t('login.connectingSso'),
      subtitleText: this.$t('login.processingRequest'),
      ticket: ''
    };
  },
  mounted() {
    this.initSso();
  },
  methods: {
    // 获取地址栏所有参数
    getQueryParams() {
      const params = {};
      // 兼容 history 和 hash 模式获取参数
      const search = window.location.search || (window.location.hash.includes('?') ? window.location.hash.split('?')[1] : '');

      if (search) {
        const urlParams = new URLSearchParams(search.startsWith('?') ? search : '?' + search);
        for (const [key, value] of urlParams.entries()) {
          params[key] = value;
        }
      }
      return params;
    },

    async initSso() {
      const params = this.getQueryParams();

      // 如果 URL 中有 lang 参数，设置语言环境
      if (params.lang) {
        this.$store.dispatch('language/setLanguage', params.lang);
        this.$i18n.locale = params.lang;
      }

      this.ticket = params.ticket;
      debugger;
      if (this.ticket) {
        // 如果有ticket，执行登录逻辑
        this.loadingText = this.$t('login.verifyingTicket');
        this.handleTicketLogin(this.ticket);
      } else {
        // 如果没有ticket，获取SSO登录地址
        this.loadingText = this.$t('login.gettingSsoUrl');
        this.redirectToSso();
      }
    },

    getSsoClientLoginUrl() {
      return `${window.location.origin}/sso-login`;
    },

    async redirectToSso() {
      try {
        const res = await getSsoAuthUrl(this.getSsoClientLoginUrl());
        if (res.code === 200 && res.data) {
          let redirectUrl = res.data;
          const params = this.getQueryParams();
          const separator = redirectUrl.includes('?') ? '&' : '?';

          // 拼接用户名
          if (params.username) {
            redirectUrl += `${separator}username=${encodeURIComponent(params.username)}`;
          }

          // 拼接当前语言环境
          const currentLang = this.$i18n.locale;
          const langSeparator = redirectUrl.includes('?') ? '&' : '?';
          redirectUrl += `${langSeparator}lang=${currentLang}&clientName=${this.$t('login.anjunKD')}`;

          // eslint-disable-next-line require-atomic-updates
          window.location.href = redirectUrl;
        } else {
          this.$message.error(res.msg || '获取认证地址失败');
          this.goBackToLogin();
        }
      } catch (error) {
        console.error('SSO Redirect Error:', error);
        this.$message.error('网络错误，请稍后再试');
        this.goBackToLogin();
      }
    },

    async handleTicketLogin(ticket) {
      try {
        await this.$store.dispatch('user/ssoLogin', ticket);

        // 记录登录日志 (参考 login/index.vue 的逻辑)
        await this.recordLoginLog();

        this.loadingText = this.$t('login.loginSuccess');
        this.subtitleText = this.$t('login.redirecting');

        setTimeout(() => {
          const params = this.getQueryParams();
          if (params.back) {
            // 如果有回跳地址，跳转到回跳地址
            window.location.href = params.back;
          } else {
            // 否则进入首页
            this.$router.push({ path: '/' });
          }
        }, 1000);
      } catch (error) {
        console.error('Ticket Login Error:', error);
        this.$message.error(typeof error === 'string' ? error : '登录验证失败');
        this.goBackToLogin();
      }
    },

    async recordLoginLog() {
      try {
        const userToken = this.$store.state.user.token.split('.')[1];
        const userStr = Base64.decode(userToken);
        const user = JSON.parse(userStr);
        const getIp = sessionStorage.getItem('ip');
        const getIpAddress = sessionStorage.getItem('ipAddress');

        const param = {
          browserInfo: computer.browserInfo,
          createUser: user.id,
          ip: getIp,
          ipAddress: getIpAddress,
          osInfo: computer.osInfo,
          source: 'SSO',
          username: user.realName
        };
        await getUserIpInformation(param);

        // 设置超时时间
        const overTime = new Date().getTime() + 12 * 60 * 60 * 1000;
        sessionStorage.setItem('overTime', overTime);
      } catch (e) {
        console.warn('Record login log failed:', e);
      }
    },

    goBackToLogin() {
      setTimeout(() => {
        this.$router.push({ path: '/login' });
      }, 2000);
    }
  }
};
</script>

<style lang="scss" scoped>
.sso-loading-container {
  height: 100vh;
  width: 100vw;
  background-color: #f8fafc; /* Slate 50 - Softer white */
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  overflow: hidden;
  position: relative;
}

/* --- Halo Background --- */
.halo-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  overflow: hidden;
}

.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.6;
  animation: floatOrb 20s infinite ease-in-out;
}

.orb-1 {
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, #e0f2fe 0%, #bae6fd 100%); /* Sky Blue */
  top: -100px;
  left: -100px;
  animation-delay: 0s;
}

.orb-2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, #f5d0fe 0%, #e879f9 100%); /* Fuchsia/Purple */
  bottom: -50px;
  right: -50px;
  animation-delay: -5s;
  opacity: 0.4;
}

.orb-3 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, #ccfbf1 0%, #5eead4 100%); /* Teal */
  top: 40%;
  left: 40%;
  animation-delay: -10s;
  opacity: 0.3;
}

@keyframes floatOrb {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -50px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.9); }
}

/* --- Particles --- */
.particles {
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
}

.particle {
  position: absolute;
  background: white;
  border-radius: 50%;
  opacity: 0.6;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.8);
  animation: floatParticle 15s infinite linear;
}

/* Generate random-looking particles using CSS classes */
.p-1 { width: 6px; height: 6px; top: 20%; left: 20%; animation-duration: 12s; animation-delay: 0s; }
.p-2 { width: 4px; height: 4px; top: 60%; left: 80%; animation-duration: 18s; animation-delay: -2s; }
.p-3 { width: 8px; height: 8px; top: 40%; left: 40%; animation-duration: 25s; animation-delay: -5s; opacity: 0.3; }
.p-4 { width: 5px; height: 5px; top: 80%; left: 10%; animation-duration: 20s; animation-delay: -8s; }
.p-5 { width: 3px; height: 3px; top: 10%; left: 90%; animation-duration: 15s; animation-delay: -1s; }
.p-6 { width: 7px; height: 7px; top: 50%; left: 50%; animation-duration: 22s; animation-delay: -4s; opacity: 0.4; }
.p-7 { width: 4px; height: 4px; top: 30%; left: 70%; animation-duration: 17s; animation-delay: -6s; }
.p-8 { width: 6px; height: 6px; top: 90%; left: 30%; animation-duration: 28s; animation-delay: -3s; }
.p-9 { width: 5px; height: 5px; top: 15%; left: 50%; animation-duration: 14s; animation-delay: -7s; }
.p-10 { width: 3px; height: 3px; top: 75%; left: 60%; animation-duration: 19s; animation-delay: -9s; }
.p-11 { width: 8px; height: 8px; top: 55%; left: 15%; animation-duration: 24s; animation-delay: -2s; opacity: 0.2; }
.p-12 { width: 4px; height: 4px; top: 5%; left: 35%; animation-duration: 16s; animation-delay: -5s; }

@keyframes floatParticle {
  0% { transform: translateY(0) translateX(0); opacity: 0; }
  10% { opacity: 0.8; }
  90% { opacity: 0.8; }
  100% { transform: translateY(-100px) translateX(20px); opacity: 0; }
}

/* --- Glass Card --- */
.glass-card {
  position: relative;
  z-index: 10;
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  padding: 60px 80px;
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow:
    0 20px 50px rgba(0, 0, 0, 0.05),
    0 1px 3px rgba(0, 0, 0, 0.1),
    inset 0 0 0 1px rgba(255, 255, 255, 0.5);
  animation: cardFloat 6s ease-in-out infinite;
}

@keyframes cardFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.loader-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
}

/* Geometric Cube Loader */
.cube-loader {
  width: 60px;
  height: 60px;
  position: relative;
  transform: rotate(45deg);
}

.cube {
  position: absolute;
  width: 24px;
  height: 24px;
  background-color: #3b82f6; /* Blue 500 - More vibrant */
  border-radius: 4px;
  animation: cubeFold 2.4s infinite linear both;
  transform-origin: 100% 100%;
  box-shadow: 0 0 10px rgba(59, 130, 246, 0.5); /* Glow effect */
}

.cube2 {
  transform: scale(1.1) rotateZ(90deg);
  background-color: #6366f1; /* Indigo 500 */
}

.cube3 {
  transform: scale(1.1) rotateZ(180deg);
  background-color: #8b5cf6; /* Violet 500 */
}

.cube4 {
  transform: scale(1.1) rotateZ(270deg);
  background-color: #ec4899; /* Pink 500 */
}

.cube2:before { animation-delay: 0.3s; }
.cube3:before { animation-delay: 0.6s; }
.cube4:before { animation-delay: 0.9s; }

@keyframes cubeFold {
  0%, 10% {
    transform: perspective(140px) rotateX(-180deg);
    opacity: 0;
  }
  25%, 75% {
    transform: perspective(140px) rotateX(0deg);
    opacity: 1;
  }
  90%, 100% {
    transform: perspective(140px) rotateY(180deg);
    opacity: 0;
  }
}

/* Text Styling */
.text-area {
  text-align: center;

  .status-title {
    font-size: 18px;
    font-weight: 600;
    color: #1e293b; /* Slate 800 */
    margin: 0 0 8px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  .status-subtitle {
    font-size: 14px;
    color: #64748b; /* Slate 500 */
    margin: 0;
    font-weight: 400;
  }
}
</style>
